// Build identification for the running SPA bundle, plus a fetcher for the
// backend's matching /api/version. Front and back ship on independent
// cadences — the About dialog displays both so the user sees what they're
// actually running.
//
// The four VITE_* env vars are injected at build time by deploy/deploy-frontend.sh
// (and the deploy/build-backend.sh sister job for parity with the backend
// args). Vite auto-exposes any process.env key prefixed `VITE_` to client
// code via import.meta.env. Missing values fall back to '0.0.0-dev' /
// 'unknown' / 'dev' so the dialog still renders during local dev or when a
// bundle gets shipped without the env vars set.

import { API_ORIGIN } from './client';
import { versionPath } from '@voicedojo/backend-types/public';
import type { VersionResponse } from '@voicedojo/backend-types/responses';

export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? '0.0.0-dev';
export const BUILD_SHA: string = import.meta.env.VITE_BUILD_SHA ?? 'unknown';
export const BUILD_TIME: string = import.meta.env.VITE_BUILD_TIME ?? 'unknown';

const rawChannel = import.meta.env.VITE_BUILD_CHANNEL ?? 'dev';
// Anything that isn't exactly 'production' is treated as dev. Keeps the type
// honest: a typo in the env var should NOT silently render the production UI.
export const BUILD_CHANNEL: 'production' | 'dev' = rawChannel === 'production' ? 'production' : 'dev';

// The backend's /api/version wire shape — the generated contract-read response
// type, re-exported under the local name existing consumers (VersionDialog)
// import. Single source of truth; no hand-mirrored interface to drift.
export type ApiVersion = VersionResponse;

// Module-level memo of a SUCCESSFUL /api/version response. The backend's version
// is immutable for the lifetime of a running bundle, so the About dialog's
// repeat opens (and any other caller) reuse the first good result instead of
// re-fetching every time the dialog is shown. Only successes are cached: a
// network error / non-OK response leaves the memo null so the next open retries.
// A fresh bundle (the redeploy case) reloads the module, clearing the memo.
let apiVersionMemo: ApiVersion | null = null;

/**
 * Fetches the running backend's /api/version. On `API_ORIGIN`. Anonymous endpoint — no cookie / credentials required.
 *
 * Memoised: the first successful fetch is cached module-side and reused on
 * subsequent calls (the About dialog re-opens cheaply). Failures are not cached.
 *
 * Throws on network failure or non-OK response. Callers (VersionDialog) catch
 * and render an error row rather than swallowing silently.
 */
export async function fetchApiVersion(): Promise<ApiVersion> {
  if (apiVersionMemo) return apiVersionMemo;
  // Route from the generated contract-read path builder (`/version`, no `/api`
  // prefix — see wiki/feature-contract-reads.md §4); no route string is
  // hand-written here. This stays a raw anonymous `fetch` (not the `api`
  // client) on purpose: it is the backend-recovery/liveness probe, so it must
  // answer with no principal and outside the app's auth/toast machinery.
  const response = await fetch(`${API_ORIGIN}/api${versionPath()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`GET /api/version returned ${response.status}`);
  }
  const body = (await response.json()) as VersionResponse;
  apiVersionMemo = body;
  return body;
}
